/** Quantidade de vias e rótulos para receituários especiais (controle, antimicrobiano, etc.). */

import type { SncrReceitaTipo } from '../services/sncrService';
import { resolveEffectivePrescriptionType } from './prescriptionTypes';
import {
  resolveItemSncrTipo,
  SNCR_NOTIFICATION_TIPOS,
  SNCR_TIPO_ORDER,
  tipoFromPrescriptionType,
} from './prescriptionItemControlLabel';

export function resolvePrescriptionViaCount(
  prescriptionType?: string | null,
  items?: Array<{ is_controlled?: boolean; is_antimicrobial?: boolean }> | null,
): number {
  const type = resolveEffectivePrescriptionType(prescriptionType, items);
  if (type === 'controlled_yellow') return 3;
  if (['controlled_blue', 'special_white', 'special_control', 'antimicrobial', 'dental'].includes(type)) {
    return 2;
  }
  return 1;
}

export function getPrescriptionViaLabels(viaCount: number): string[] {
  if (viaCount >= 3) {
    return ['1ª Via — Farmácia', '2ª Via — Notificação', '3ª Via — Paciente'];
  }
  if (viaCount === 2) {
    return ['1ª Via — Farmácia (retenção)', '2ª Via — Paciente'];
  }
  return [];
}

export function effectivePrescriptionViaCount(
  prescription: { via_count?: number | null; prescription_type?: string | null; items?: any[] | null },
): number {
  const items = Array.isArray(prescription?.items) ? prescription.items : [];
  const resolved = resolvePrescriptionViaCount(prescription?.prescription_type, items);
  // Com itens estruturados, o tipo/vias vêm dos medicamentos (via_count antigo pode estar desatualizado).
  if (items.length > 0) return resolved;

  const stored = Number(prescription?.via_count);
  if (!Number.isFinite(stored) || stored < 1) return resolved;
  return Math.max(stored, resolved);
}

/** RCE comporta até 3 substâncias por receita (Portaria SVS/MS 344/98). */
const RCE_MAX_ITEMS = 3;

const TIPO_LEGACY_PRESCRIPTION_TYPE: Record<SncrReceitaTipo, string> = {
  RCE: 'special_control',
  RET: 'antimicrobial',
  NRA: 'controlled_yellow',
  NRB: 'controlled_blue',
  NRB2: 'controlled_blue',
  NRR: 'special_white',
  NRT: 'special_white',
};

/** Notificação vai inteira para a farmácia; RCE e antimicrobiano têm via de retenção e via do paciente. */
export function paperViaCountFor(tipo: SncrReceitaTipo): number {
  return SNCR_NOTIFICATION_TIPOS.includes(tipo) ? 1 : 2;
}

export type PrescriptionViaBlock = {
  items: any[];
  viaCount: number;
  prescriptionType: string;
  receitaTipo: SncrReceitaTipo | null;
};

/**
 * Divide o receituário nos documentos que a lei exige separados (Portaria
 * 344/98, RDC 471/2021): comuns em via única; antimicrobiano em receita
 * própria, sem controlados junto; RCE com até três substâncias; cada
 * notificação (A, B, B2, retinoides, talidomida) com uma única substância.
 * Mesmo agrupamento da numeração SNCR no backend.
 */
export function splitPrescriptionByViaRequirement(prescription: {
  prescription_type?: string | null;
  via_count?: number | null;
  format?: string | null;
  items?: any[] | null;
}): PrescriptionViaBlock[] {
  const items = Array.isArray(prescription?.items) ? prescription.items : [];
  if (String(prescription?.format || '') === 'free_text' || !items.length) {
    const tipo = tipoFromPrescriptionType(prescription?.prescription_type);
    return [{
      items,
      viaCount: tipo ? paperViaCountFor(tipo) : effectivePrescriptionViaCount(prescription),
      prescriptionType: resolveEffectivePrescriptionType(prescription?.prescription_type, items),
      receitaTipo: tipo,
    }];
  }

  const plain: any[] = [];
  const byTipo = new Map<SncrReceitaTipo, any[]>();
  items.forEach((item) => {
    const tipo = resolveItemSncrTipo(item, prescription?.prescription_type);
    if (!tipo) {
      plain.push(item);
      return;
    }
    if (!byTipo.has(tipo)) byTipo.set(tipo, []);
    byTipo.get(tipo)!.push(item);
  });

  const blocks: PrescriptionViaBlock[] = [];
  if (plain.length) blocks.push({ items: plain, viaCount: 1, prescriptionType: 'simple', receitaTipo: null });
  SNCR_TIPO_ORDER.forEach((tipo) => {
    const list = byTipo.get(tipo);
    if (!list?.length) return;
    const groups: any[][] = [];
    if (SNCR_NOTIFICATION_TIPOS.includes(tipo)) {
      list.forEach((item) => groups.push([item]));
    } else if (tipo === 'RCE') {
      for (let i = 0; i < list.length; i += RCE_MAX_ITEMS) groups.push(list.slice(i, i + RCE_MAX_ITEMS));
    } else {
      groups.push(list);
    }
    groups.forEach((group) => {
      blocks.push({
        items: group,
        viaCount: paperViaCountFor(tipo),
        prescriptionType: TIPO_LEGACY_PRESCRIPTION_TYPE[tipo],
        receitaTipo: tipo,
      });
    });
  });
  return blocks;
}
